import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types for our state
export interface ConflictScenario {
	scenarioDescription: string;
	similarPrompts: {
		workflowId: string;
		workflowName: string;
		scenarioIndex: number;
		description: string;
		similarity?: number;
	}[];
}

export interface PromptImprovement {
	workflowId: string;
	workflowName: string;
	scenarioIndex: number;
	currentDescription: string;
	suggestedDescription: string;
	workflowType: string;
}

export interface WorkflowIssuesState {
	conflicts: ConflictScenario[];
	improvements: PromptImprovement[];
	isLoading: boolean;
	error: string | null;
	lastUpdated: string | null;
}

const initialState: WorkflowIssuesState = {
	conflicts: [],
	improvements: [],
	isLoading: false,
	error: null,
	lastUpdated: null,
};

// Create the slice
export const slice = createSlice({
	name: 'workflowIssues',
	initialState,
	reducers: {
		setWorkflowIssues(
			state,
			action: PayloadAction<{
				conflicts: ConflictScenario[];
				improvements: PromptImprovement[];
			}>,
		) {
			state.conflicts = action.payload.conflicts;
			state.improvements = action.payload.improvements;
			state.isLoading = false;
			state.error = null;
			state.lastUpdated = new Date().toISOString();
		},
		setLoadingState(state, action: PayloadAction<boolean>) {
			state.isLoading = action.payload;
			if (action.payload) {
				state.error = null;
			}
		},
		setError(state, action: PayloadAction<string>) {
			state.isLoading = false;
			state.error = action.payload;
		},
		applyImprovement(
			state,
			action: PayloadAction<{
				workflowId: string;
				scenarioIndex: number;
				newDescription: string;
			}>,
		) {
			// Find and remove the applied improvement
			state.improvements = state.improvements.filter(
				(improvement) =>
					!(
						improvement.workflowId === action.payload.workflowId &&
						improvement.scenarioIndex === action.payload.scenarioIndex
					),
			);
		},
		rejectImprovement(
			state,
			action: PayloadAction<{
				workflowId: string;
				scenarioIndex: number;
			}>,
		) {
			// Find and remove the rejected improvement
			state.improvements = state.improvements.filter(
				(improvement) =>
					!(
						improvement.workflowId === action.payload.workflowId &&
						improvement.scenarioIndex === action.payload.scenarioIndex
					),
			);
		},
		removeConflict(
			state,
			action: PayloadAction<{
				scenarioDescription: string;
				conflictingWorkflowId: string;
				conflictingScenarioIndex: number;
			}>,
		) {
			// Find the conflict scenario
			const conflictIndex = state.conflicts.findIndex(
				(conflict) => conflict.scenarioDescription === action.payload.scenarioDescription,
			);

			if (conflictIndex !== -1) {
				// Remove the specific similar prompt
				state.conflicts[conflictIndex].similarPrompts = state.conflicts[
					conflictIndex
				].similarPrompts.filter(
					(prompt) =>
						!(
							prompt.workflowId === action.payload.conflictingWorkflowId &&
							prompt.scenarioIndex === action.payload.conflictingScenarioIndex
						),
				);

				// If no more similar prompts exist, remove the entire conflict scenario
				if (state.conflicts[conflictIndex].similarPrompts.length === 0) {
					state.conflicts.splice(conflictIndex, 1);
				}
			}
		},
	},
});

// Export actions
export const {
	setWorkflowIssues,
	setLoadingState,
	setError,
	applyImprovement,
	rejectImprovement,
	removeConflict,
} = slice.actions;

// Export reducer
export default slice.reducer;
